import React from 'react';
import { useNavigate } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import './HistoryTrainCategories.css';

const HistoryTrainCategories = () => {
    const navigate = useNavigate();

    // Historical trainer category options with themed groups
    const historyTrainerCategories = [
        {
            id: 'history-one-trainers',
            title: 'Ancient Conquerors & Empire Builders',
            description: 'Train with legendary generals, emperors, and world conquerors',
            image: '/assets/images/history-one-trainers.jpg',
            route: '/history-one-trainers',
            color: '#4B0082', // Imperial purple
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Phalanx Formation Power', 'Roman Legion Discipline', 'Tactical Genius']
        },
        {
            id: 'history-two-trainers',
            title: 'Renaissance Masters & Scientific Visionaries',
            description: 'Train with legendary artists, inventors, and revolutionary thinkers',
            image: '/assets/images/history-two-trainers.jpg',
            route: '/history-two-trainers',
            color: '#1E3A8A', // Renaissance blue
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Sculptural Power Endurance', 'Scientific Method', 'Artistic Innovation']
        },
        {
            id: 'history-three-trainers',
            title: 'Revolutionary Spirits & Freedom Fighters',
            description: 'Train with legendary rebels, warriors, and liberators who fought for freedom',
            image: '/assets/images/history-three-trainers.jpg',
            route: '/history-three-trainers',
            color: '#DC143C', // Revolutionary red
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Chain Breaking Power', 'Celtic Vengeance', 'Spartan Last Stand']
        },
        {
            id: 'history-four-trainers',
            title: 'Explorers & Adventurous Souls',
            description: 'Train with legendary explorers, navigators, and frontier pioneers',
            image: '/assets/images/history-four-trainers.jpg',
            route: '/history-four-trainers',
            color: '#006994', // Ocean blue
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Silk Road Endurance', 'Ocean Navigation', 'Antarctic Leadership']
        },
        {
            id: 'history-five-trainers',
            title: 'Literary Giants & Cultural Legends',
            description: 'Train with legendary writers, artists, musicians, and cultural icons',
            image: '/assets/images/history-five-trainers.jpg',
            route: '/history-five-trainers',
            color: '#8B008B', // Literary magenta
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Gothic Dark Beauty', 'Aesthetic Philosophy', 'Musical Prodigy Power']
        }
    ];

    // Handle trainer category selection
    const handleCategorySelect = (category) => {
        console.log('History trainer category clicked:', category);

        // 🎵 PLAY CATEGORY-SPECIFIC SOUND
        if (category.sound) {
            soundManager.playSound(category.sound, 0.7);
        } else {
            soundManager.playButtonSound();
        }

        // Navigate to the specific History trainer category page
        navigate(category.route);
    };

    // Handle back to home
    const handleBackToHome = () => {
        soundManager.playButtonSound();
        navigate('/');
    };

    return (
        <div className="history-train-categories-container">
            <div className="history-animated-background"></div>

            <header className="history-train-categories-header">
                <button
                    className="back-to-home-button history-back-button"
                    onClick={handleBackToHome}
                >
                    ← Back to Home
                </button>

                <div className="header-content">
                    <h1>Historical Training Legends</h1>
                    <p>Choose your path to train with history's greatest figures across all civilizations</p>
                    <div className="history-symbol">🏛️</div>
                </div>
            </header>

            <div className="history-trainer-categories-grid">
                {historyTrainerCategories.map((category) => (
                    <div
                        key={category.id}
                        className="history-trainer-category-card-link"
                        onClick={() => handleCategorySelect(category)}
                    >
                        <div
                            className="history-trainer-category-card"
                            data-category-id={category.id}
                            style={{
                                '--history-theme-color': category.color
                            }}
                        >
                            <div className="history-trainer-category-image">
                                <div
                                    className="history-image-placeholder"
                                    style={{
                                        backgroundColor: category.color,
                                        backgroundImage: `url(${category.image})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                >
                                    <div className="history-trainer-count-badge">
                                        {category.trainerCount} Legends
                                    </div>
                                    <div className="history-overlay"></div>
                                </div>
                            </div>
                            <div className="history-trainer-category-info">
                                <h2 style={{ color: category.color }}>{category.title}</h2>
                                <p>{category.description}</p>
                                <div className="history-category-features">
                                    {category.features.map((feature, index) => (
                                        <span key={index} className="history-feature-badge">
                                            ⚔️ {feature}
                                        </span>
                                    ))}
                                </div>
                                <div className="history-theme-indicator" style={{ backgroundColor: category.color }}></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <footer className="history-train-categories-footer">
                <div className="history-train-info">
                    <h3>🏛️ Historical Training Philosophy</h3>
                    <div className="history-info-grid">
                        <div className="history-info-item">
                            <span className="history-info-icon">👑</span>
                            <div>
                                <strong>Legendary Figures</strong>
                                <p>Train with 30 legendary figures from across human history and civilization</p>
                            </div>
                        </div>
                        <div className="history-info-item">
                            <span className="history-info-icon">⚔️</span>
                            <div>
                                <strong>Strength Through History</strong>
                                <p>Experience training methods that built empires, created art, and changed the world</p>
                            </div>
                        </div>
                        <div className="history-info-item">
                            <span className="history-info-icon">🌍</span>
                            <div>
                                <strong>Global Heritage</strong>
                                <p>Connect with training wisdom from Ancient Rome to Renaissance Italy to the American Frontier</p>
                            </div>
                        </div>
                        <div className="history-info-item">
                            <span className="history-info-icon">🎯</span>
                            <div>
                                <strong>Diverse Disciplines</strong>
                                <p>From gladiator combat to artistic genius, explore every aspect of human excellence</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="history-flow-info">
                    <div className="history-divider"></div>
                    <p>💡 <strong>Path:</strong> Home → History Train → Categories → Trainer → Intensity → Workout</p>
                    <p className="history-wisdom">"I came, I saw, I conquered" - Julius Caesar</p>
                </div>
            </footer>
        </div>
    );
};

export default HistoryTrainCategories;